import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { META_PIXEL, getIntegration } from '../../services/integrationService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme, MetaPixelEvents } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { FloatingSettings } from '../../components/landings/FloatingSettings';
import { BackgroundSettings } from '../../components/landings/BackgroundSettings';
import { MetaPixelSettings } from '../../components/landings/MetaPixelSettings';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { DEFAULT_REPLY_NOTE, DEFAULT_WHATSAPP_MESSAGE } from '../../data/thankYou';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
import { normalizeSlug, validateSlug } from '../../config/app';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /**
   * Estado de publicación, distinto del de guardado:
   * - 'limpio': nada pendiente.
   * - 'pendiente': hay cambios guardados que aún no se han publicado.
   * - 'publicado': la última acción fue publicar.
   */
  const [publishState, setPublishState] = useState<'limpio' | 'pendiente' | 'publicado'>('limpio');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  /**
   * Pixel global de la organización, leído una vez para poder RESINCRONIZAR
   * de verdad cuando el usuario vuelve a elegir «Usar el Pixel global» en
   * esta landing (ver MetaPixelSettings). Sin esto, volver a «global» solo
   * cambiaba una etiqueta y dejaba pegado el Pixel propio anterior.
   */
  const [orgMetaPixel, setOrgMetaPixel] = useState<{
    enabled: boolean;
    pixelId?: string;
    events?: MetaPixelEvents;
  } | null>(null);
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  useEffect(() => {
    if (!organization?.id) return;
    let active = true;
    getIntegration(organization.id, META_PIXEL).then(data => {
      if (!active) return;
      const config = (data?.config ?? {}) as {
        pixelId?: string;
        events?: MetaPixelEvents;
      };
      setOrgMetaPixel({
        enabled: Boolean(data?.isActive),
        pixelId: config.pixelId,
        events: config.events
      });
    });
    return () => {
      active = false;
    };
  }, [organization?.id]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-1387" data-mp="uwtysni1snyj">
        <p className="text-sm text-muted-foreground" data-id="element-1388" data-mp="zv58my8ceygr">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-1389" data-mp="lvdyj8ky6281">
          <Button variant="secondary" data-id="element-1390" data-mp="pv4lt2b9m07t">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  /*
    La vista previa debe resolver el logotipo con la MISMA cadena que la landing
    pública (`components/public/LandingView.tsx`): logo propio de la landing →
    logo de la empresa → identificador de Sistema AVA. Antes el constructor
    pasaba `draft` sin resolver, así que mostraba el identificador genérico
    aunque la empresa tuviera logotipo, y la página pública mostraba otro.
  */
  const previewLanding: LandingPage = {
    ...draft,
    pageConfig: {
      ...draft.pageConfig,
      logoUrl: draft.pageConfig.logoUrl || organization?.brand.logoUrl || organization?.logoUrl
    }
  };
  // Disponibilidad del enlace público: se comprueba contra el resto de landings.
  const slugCheck = validateSlug(draft.slug, landings.filter(l => l.id !== draft.id).map(l => l.slug));
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
    setPublishState('pendiente');
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    // El enlace público debe ser válido y único antes de guardar o publicar.
    const check = validateSlug(draft!.slug, landings.filter(l => l.id !== draft!.id).map(l => l.slug));
    if (!check.valid) {
      setSaveError(check.reason ?? 'El enlace no es válido.');
      toast.error('Revisa el enlace público', {
        description: check.reason
      });
      return;
    }
    setSaving(true);
    setSaveError(null);
    // Instrumentación: del clic hasta que la interfaz vuelve a estar lista.
    const clicked = performance.now();
    try {
      /*
        Se guarda el logotipo ya resuelto. La tabla `organizations` no es
        legible por un visitante anónimo, así que la landing pública no puede
        recurrir al logo de la empresa: si la landing no guarda su propia URL,
        el móvil se queda sin logotipo aunque el constructor lo muestre.
      */
      await saveLanding(previewLanding);
      if (status) {
        await setLandingStatus(draft!.id, status);
        update({
          status
        });
      }
      setSaved(true);
      // Guardar nunca cambia de página ni recarga: solo confirma.
      if (status === 'publicada') {
        setPublishState('publicado');
        toast.success('Landing publicada correctamente', {
          description: landingUrl(draft!.slug)
        });
      } else if (status === 'despublicada') {
        setPublishState('pendiente');
        toast.success('Landing despublicada', {
          description: 'Su enlace público deja de estar disponible.'
        });
      } else {
        // Guardar conserva el estado actual: si estaba publicada, sigue
        // publicada y la URL pública muestra ya los cambios.
        setPublishState(draft!.status === 'publicada' ? 'publicado' : 'pendiente');
        toast.success(draft!.status === 'publicada' ? 'Cambios guardados y publicados' : 'Cambios guardados');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      setSaveError(message);
      toast.error('No se ha podido guardar', {
        description: message
      });
    } finally {
      setSaving(false);
      console.log(`[${status === 'publicada' ? 'AVA PUBLISH' : 'AVA SAVE'}] clic → interfaz lista: ${(performance.now() - clicked).toFixed(0)} ms`);
    }
  }
  /** Copia la URL pública, con confirmación visible. */
  async function handleCopyLink() {
    const ok = await copyToClipboard(landingUrl(draft!.slug));
    if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace', {
      description: landingUrl(draft!.slug)
    });
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1391" data-mp="3lce537puom5y">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1392" data-mp="3v4s9y9c868wc" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1393" data-mp="3q4ntukf8bg6n">
        <div data-id="element-1394" data-mp="32f36lpc1lwrk">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-1395" data-mp="32etjqamao5q3">
            {draft.name}
          </h1>
          <p className="break-all text-sm text-muted-foreground" data-id="element-1396" data-mp="2xi323iuud22k">
            {landingUrl(draft.slug)} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1397" data-mp="qpj1vu7x3ngq">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-1398" data-mp="qs591sqwc0yf">
              Guardando…
            </span> : !saved ? <span className="self-center text-sm text-amber-600" data-id="element-1399" data-mp="mqtd75hfijhs">
              Cambios sin guardar
            </span> : publishState === 'publicado' ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1400" data-mp="3kh8w09e0v4l8">
              <CheckIcon className="h-4 w-4" data-id="element-1401" data-mp="3pgvbkphbowx7" /> Publicado correctamente
            </span> : <span className="self-center text-sm text-amber-600" data-id="element-1402" data-mp="3tiglc1yk8v36">
              Borrador guardado · pendiente de publicar
            </span>}
          {/*
            Guardar NO cambia el estado de publicación. Antes este botón
            llamaba a handleSave('borrador'), que devolvía a borrador una
            landing ya publicada: su URL pública pasaba a responder «esta
            página no existe» en cuanto se guardaba un cambio.
           */}
          <Button variant="ghost" onClick={() => handleSave()} loading={saving} data-id="element-1403" data-mp="3alfcu75sscuh">
            Guardar cambios
          </Button>
          {draft.status === 'publicada' && <Button variant="ghost" onClick={() => handleSave('despublicada')} loading={saving} data-id="element-1404" data-mp="2i31rv85k8lct">
              Despublicar
            </Button>}
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-1406" data-mp="25l7dl26uxesz" />} onClick={() => window.open(`/${draft.slug}`, '_blank')} data-id="element-1405" data-mp="2aj97ki5tmyj8">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-1408" data-mp="21l6zxjrl17mz" />} onClick={handleCopyLink} data-id="element-1407" data-mp="21i19a03b1xp5">
            Copiar enlace
          </Button>
          <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-1409" data-mp="1rl6ftvlw2nhq">
            {draft.status === 'publicada' ? 'Actualizar publicación' : 'Publicar'}
          </Button>
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-1410" data-mp="1huv9k2eqg27o">
          <span data-id="element-1411" data-mp="18uf6gu1wix3c">
            <strong className="font-semibold" data-id="element-1412" data-mp="18uye9hxu853n">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-1413" data-mp="13wn5zbn6y2nj">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-1414" data-mp="33zy0rvgy261e">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-1415" data-mp="2u78vxjfwfqf1">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-1416" data-mp="2z6d0txvsj1zk">
            <TabsList data-id="element-1417" data-mp="2l7lr0nnyo2h8">
              <TabsTrigger value="bloques" data-id="element-1418" data-mp="2l73wbvgy5wbf">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-1419" data-mp="2p8yy112sibpq">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-1420" data-mp="2paidwqkjakqz">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-1421" data-mp="2bahe7eg09jd3">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-1422" data-mp="2bbirlpkhuhxz">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-1423" data-mp="egnru1hrqaes">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-1424" data-mp="1u0my1aho99u1">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-1425" data-mp="1lzpbfavkmb7j">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-1426" data-mp="1m21z84o0ffsi">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-1427" data-mp="20wq3jfhoc86j" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-1428" data-mp="24y1np5hs417c">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1429" data-mp="29s5vfqyvcd9h">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-1430" data-mp="2jpu1hd1xd76y">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1431" data-mp="cxnnyx3mg1mr">
                          <BookmarkIcon className="h-4 w-4" data-id="element-1432" data-mp="3u9yjwrdcnx5d" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1433" data-mp="vpgl93pq490a">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-1434" data-mp="1egpt78juippr" /> : <EyeOffIcon className="h-4 w-4" data-id="element-1435" data-mp="1ehrgqci1t4od" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1436" data-mp="19pr8lwrtr5gp">
                          <CopyIcon className="h-4 w-4" data-id="element-1437" data-mp="mswku7w5gtoi" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-1438" data-mp="92jbq27iv8xl">
                            <Trash2Icon className="h-4 w-4" data-id="element-1439" data-mp="15rr1jmf6c47b" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-1440" data-mp="rrg7kaiki9hx">
                          <Tabs defaultValue="contenido" data-id="element-1441" data-mp="wqt27hfokkv2">
                            <TabsList data-id="element-1442" data-mp="wtf8zeuqywc8">
                              <TabsTrigger value="contenido" data-id="element-1443" data-mp="3chxfnrpebgxq">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-1444" data-mp="3hha2icg9oo0b">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-1445" data-mp="39xiwhlh6s92t">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-1446" data-mp="39zvetzquwlge" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-1447" data-mp="3iygsphldvcau">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-1448" data-mp="3ixxlovx9u8bl" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1450" data-mp="2m6d82hx6k8gw" />} onClick={() => setAddOpen(true)} data-id="element-1449" data-mp="2m8pwndxg1h2x">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-1451" data-mp="2v83asjwiittz">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1452" data-mp="2v71nnh282gvw">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1453" data-mp="27hh2crilj7jg" />
                <div data-id="element-1454" data-mp="z1cc53e1x9on">
                  <Input id="l-slug" label="Slug (enlace público)" helperText={landingUrl(draft.slug)} value={draft.slug} error={slugCheck.valid ? undefined : slugCheck.reason} onChange={e => update({
                  slug: normalizeSlug(e.target.value)
                })} data-id="element-1455" data-mp="z0u5dctrjd2h" />
                  {slugCheck.valid && <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-green-600" data-id="element-1456" data-mp="13xt09xl93o1q">
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1457" data-mp="1hp7m0c6jm3ch" />
                      Enlace disponible
                    </p>}
                </div>
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-1458" data-mp="1rp8640c8knhq" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-1459" data-mp="3oijqe014qo07" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-1460" data-mp="28xr8rcrboawh" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-1461" data-mp="1z6whftz7yl46" />
                <div data-id="element-1462" data-mp="1q945mzsphvlj">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1463" data-mp="1baju4vpnh7gj">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1464" data-mp="1gaocyse16e9x" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1465" data-mp="35mqjcyfdporu">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Elementos flotantes opcionales de esta landing */}
              <BackgroundSettings value={draft.pageConfig.background} onChange={background => updateConfig({
              background
            })} data-id="element-1466" data-mp="3462wa3v97kba" />
              <FloatingSettings value={draft.pageConfig.floating} onChange={floating => updateConfig({
              floating
            })} data-id="element-1467" data-mp="2v77owogh446e" />
              <MetaPixelSettings value={draft.pageConfig.metaPixel} orgDefault={orgMetaPixel ?? undefined} onChange={metaPixel => updateConfig({
              metaPixel
            })} data-id="element-1468" data-mp="3rwgnxt9xfh5c" />

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1469" data-mp="3e5k00fqy3o4m">
                <div className="mb-3 flex items-center gap-2" data-id="element-1470" data-mp="3e3r2bpyb8e8r">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1471" data-mp="ejn3pa67xf0c" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1472" data-mp="9qaqdw6zoumw">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1473" data-mp="1xcexma8ji879">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1474" data-mp="26e4wg14r49f2">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1475" data-mp="2bd7s4uyud3ad">
                        <span className="min-w-0 flex-1" data-id="element-1476" data-mp="2gb9afdog0yks">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1477" data-mp="2g9ptdkuyqjhy">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1478" data-mp="2p3userk9318k">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1479" data-mp="2z3bsmtgqgmx5">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1480" data-mp="2gfp1yyshft77">
                          <Trash2Icon className="h-4 w-4" data-id="element-1481" data-mp="2q7l5ipdc8bit" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1482" data-mp="1xinxrg6r5x4b">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1483" data-mp="1xjp81ify2fkz">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1484" data-mp="2cgpzrly4yajg" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1485" data-mp="3iu5w3nlzojlb" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1486" data-mp="3hetlridaex80" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1487" data-mp="hzg9wgbfqyxe" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1488" data-mp="vy65frilwqjn" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1489" data-mp="16l4gvxepv1p7" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1490" data-mp="ron9fh9ne77x" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1491" data-mp="dwytt406x0m7" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1492" data-mp="3q0rstcz004u1" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1493" data-mp="8nxqmz8zf9d8" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1494" data-mp="3lwtv96pbn233" />
                {draft.pageConfig.thankYou.showWhatsapp && <>
                    <Input id="t-waeyebrow" label="Texto pequeño superior" placeholder="Solo falta un paso" value={draft.pageConfig.thankYou.whatsappEyebrow ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappEyebrow: e.target.value
                  }
                })} data-id="element-1495" data-mp="bjo8n8steqdb" />
                    <Input id="t-watitle" label="Título del paso de WhatsApp" placeholder="Abre WhatsApp" value={draft.pageConfig.thankYou.whatsappStepTitle ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappStepTitle: e.target.value
                  }
                })} data-id="element-1496" data-mp="uf4v62dbwxzr" />
                    <Input id="t-watext" label="Texto del paso de WhatsApp" placeholder="Tu solicitud ya está preparada." value={draft.pageConfig.thankYou.whatsappStepText ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappStepText: e.target.value
                  }
                })} data-id="element-1497" data-mp="1e3nbdugcx12b" />
                    <Input id="t-walabel" label="Texto del botón" placeholder="Continuar por WhatsApp" value={draft.pageConfig.thankYou.whatsappLabel ?? ''} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappLabel: e.target.value
                  }
                })} data-id="element-1498" data-mp="1ithfvndjnvz6" />
                    <p className="text-xs leading-relaxed text-muted-foreground" data-id="element-1499" data-mp="1dvzgoopbwv4d">
                      Variables disponibles: {'{{nombre}} {{empresa}} {{telefono}} {{correo}} {{ciudad}} {{instagram}} {{descripcion}} {{servicio}} {{publicidad}} {{presupuesto}} {{comentarios}}'}. Las líneas cuyas variables queden vacías se eliminan solas.
                    </p>
                    <Textarea id="t-wamsg" rows={10} label="Mensaje automático" value={draft.pageConfig.thankYou.whatsappMessage ?? DEFAULT_WHATSAPP_MESSAGE} onChange={e => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    whatsappMessage: e.target.value
                  }
                })} data-id="element-1500" data-mp="v2c1zdr5zdri" />
                    <Switch id="t-wapreview" label="Mostrar vista previa del mensaje" checked={draft.pageConfig.thankYou.showWhatsappPreview !== false} onChange={checked => updateConfig({
                  thankYou: {
                    ...draft.pageConfig.thankYou,
                    showWhatsappPreview: checked
                  }
                })} data-id="element-1501" data-mp="14yh5ooo8vl7i" />
                  </>}
                <Switch id="t-replynote" label="Mostrar el plazo de respuesta" checked={draft.pageConfig.thankYou.showReplyNote !== false} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showReplyNote: checked
                }
              })} data-id="element-1502" data-mp="1jpzgm828ypk0" />
                {draft.pageConfig.thankYou.showReplyNote !== false && <Input id="t-replytext" label="Plazo de respuesta" placeholder={DEFAULT_REPLY_NOTE} value={draft.pageConfig.thankYou.replyNote ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  replyNote: e.target.value
                }
              })} data-id="element-1503" data-mp="3i6qi6ouyvujo" />}
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1504" data-mp="4qaojm95l83y" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1505" data-mp="3n51s0zl9i51w" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1506" data-mp="trzyhh3bm736">
          <DevicePreview device={device} onDeviceChange={setDevice} onBlockSelect={setSelectedBlockId} isolate toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1508" data-mp="jtk7de4amxto">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1509" data-mp="otgc5fktetaa">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1510" data-mp="av8fz9vlrw06">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1507" data-mp="trhw02q2ewmt">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} fallbackPhone={draft.pageConfig.floating?.whatsapp?.phone} preview data-id="element-1511" data-mp="257evulzfz0yt" /> : <LandingRenderer landing={previewLanding} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} viewport={device === 'mobile' || device === 'iphone16' ? 'mobile' : device === 'tablet' ? 'tablet' : 'desktop'} brandName={organization?.name} data-id="element-1512" data-mp="3pphbveem902i" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1513" data-mp="h7kutlliad2h">
        {components.length > 0 && <div className="mb-6" data-id="element-1514" data-mp="c9rrr1fi1z6f">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1515" data-mp="q9u7ah0c4gii">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1516" data-mp="lc0wtbs010cf">
              {components.map(component => <li key={component.id} data-id="element-1517" data-mp="2s28i6w4gokpf">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1518" data-mp="2s39xxdwf9vdh">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1519" data-mp="2ticoidaoa6tj">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1520" data-mp="2yiqulpspr6ox">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1521" data-mp="25ul6qu6d51kv">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1522" data-mp="2fqfx6bgtw0y7">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1523" data-mp="2kho6z8m2lobw">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1524" data-mp="2kiprs4sw1p6t">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-1525" data-mp="1rzsmxjjfwz8h">
                  {definition.label}
                  {definition.category && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary" data-id="element-1526" data-mp="1fj14lzwx2lbf">
                      {definition.category}
                    </span>}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1527" data-mp="15jsa95y534oj">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1529" data-mp="rqu9t56uame4">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1530" data-mp="hur983rmyvta">Guardar componente</Button>
          </>} data-id="element-1528" data-mp="wri17ryzd58v">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1531" data-mp="dte11n12mgj7" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1532" data-mp="378zgqz7vb00">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}